import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import PlainDate from '@Domain/PlainDate';
import FetchRecordingPolicy from '@Application/RecordingPolicy/Queries/FetchRecordingPolicy';
import fetchRecordingPolicy from '@Application/RecordingPolicy/fetchRecordingPolicy';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useAudioFilePort from '@UI/Providers/PortsProvider/Hooks/AudioFIle/useAudioFilePort';
import useLocalBufferPort from '@UI/Providers/PortsProvider/Hooks/LocalBuffer/useLocalBufferPort';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import type RecordingPolicyView from '@UI/Store/RecordingPolicy/Views/RecordingPolicyView';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

/** The one message this hook shows for a local port failure that no `*Notifications` listener ever
 * sees (`createItem`/`appendChunk`/`markReady`/`LocalInterviewPort.create` never go through
 * `synapse`) — mirrors `useInterviewCapture.ts`'s `ITEM_PREPARATION_FAILURE_MESSAGE`. */
const PREPARATION_FAILURE_MESSAGE = "Ce fichier n'a pas pu être préparé pour le transfert.";

export type ImportRejectionReason = 'unsupported-format' | 'unreadable' | 'too-large';

type UseInterviewImportParams = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	/** The preparation the imported interview is attached to. */
	readonly preparationId: number;
	readonly ownerUserId: number | null;
	/** Called once the file has been handed over to the transfer queue. */
	readonly onImported: () => void;
};

type UseInterviewImportResult = {
	readonly policy: RecordingPolicyView | null;
	readonly policyError: Error | null;
	readonly refetchPolicy: () => void;
	readonly rejection: ImportRejectionReason | null;
	readonly preparationError: string | null;
	readonly preparingProgress: number | null;
	readonly handleFileSelected: (file: File) => void;
};

/**
 * Orchestrates the import of an audio file into a preparation: no consent screen on this path — an
 * imported file presumes the consent was collected outside the application, so the local interview it
 * creates carries `consentGrantedAt: null` and `source: 'imported'`, dated today and named « Entretien
 * importé du {date} », with the preparation written on it so it travels to the server creation.
 *
 * The whole chain from `LocalInterviewPort.create` to `markReady` runs inside one continuous call
 * triggered by a single file drop, with no intervening page load, so there is no resumable "already
 * created" branch to check here. The queue takes over after `markReady`; `onImported` then lets the
 * caller leave the import.
 */
function useInterviewImport(params: UseInterviewImportParams): UseInterviewImportResult {
	const { facilityId, subject, preparationId, ownerUserId, onImported } = params;

	const dispatch = useDispatch();
	const audioFilePort = useAudioFilePort();
	const localBufferPort = useLocalBufferPort();
	const localInterviewPort = useLocalInterviewPort();
	const outboxPort = useOutboxPort();

	const [rejection, setRejection] = React.useState<ImportRejectionReason | null>(null);
	const [preparationError, setPreparationError] = React.useState<string | null>(null);
	const [preparingProgress, setPreparingProgress] = React.useState<number | null>(null);

	// Step 1 — no limit is ever announced before the policy is known; no `cache` (`guides/state-stores.md`
	// default preset): this View carries its whole scope on its own (a referential singleton).
	const {
		data: policy,
		error: policyError,
		refetch: refetchPolicy,
	} = useStoreQuery({
		selector: (state) => state.recordingPolicyStore.policy,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchRecordingPolicy(new FetchRecordingPolicy())),
	});

	const runImport = React.useCallback(
		async (file: File, mimeType: string, durationMs: number | null): Promise<void> => {
			if (ownerUserId === null || policy === null) {
				return;
			}

			setPreparingProgress(0);

			try {
				const today = PlainDate.today();
				const localInterview = await localInterviewPort.create({
					ownerUserId,
					facilityId,
					subject,
					name: `Entretien importé du ${today.toDisplay()}`,
					kind: InterviewKind.CONVERSATION,
					conductedOn: today.toApi(),
					participants: [],
					consentGrantedAt: null,
					source: 'imported',
					preparationId,
				});

				// Step 6 — asked at every import, never behind a "already requested" flag
				// (`LocalBufferPort.ts`); a rejection here never blocks the import itself.
				void localBufferPort.requestPersistence().catch(() => undefined);

				// Step 7/8 — `createItem` before any write to the buffer: `itemId` does not exist
				// before it resolves (`OutboxPort.ts`). No `retentionMs` here: the engine carries it.
				const item = await outboxPort.createItem({
					kind: 'interview-recording',
					mimeType,
					context: {
						facilityId,
						subjectTypeKey: subject.typeKey,
						subjectId: subject.subjectId,
						localInterviewId: localInterview.id,
					},
					ownerUserId,
				});

				// Closes the reopening this local interview could otherwise offer: without it,
				// `outboxItemId` would stay `null` and the import would look `'capturable'` again.
				await localInterviewPort.attachOutboxItem(localInterview.id, item.id);

				// Step 9 — one chunk per segment, segments contiguous from zero (A-07): a whole
				// imported file is one container, unlike a capture's per-segment restarted recorder.
				const segmentMaxBytes = policy.segmentMaxBytes;
				const totalSegments = Math.max(1, Math.ceil(file.size / segmentMaxBytes));

				for (let segmentIndex = 0; segmentIndex < totalSegments; segmentIndex += 1) {
					const start = segmentIndex * segmentMaxBytes;
					const end = Math.min(start + segmentMaxBytes, file.size);

					// `File.slice()` is lazy — it never reads bytes into memory here, which is what
					// keeps this loop's footprint bounded regardless of the file's total size.
					await localBufferPort.appendChunk(item.id, segmentIndex, 0, file.slice(start, end));

					setPreparingProgress((segmentIndex + 1) / totalSegments);
				}

				// Step 10 — the queue takes over from here.
				await outboxPort.markReady(item.id, { durationMs });

				onImported();
			} catch (error) {
				setPreparationError(PREPARATION_FAILURE_MESSAGE);
				throw error;
			} finally {
				setPreparingProgress(null);
			}
		},
		[
			ownerUserId,
			policy,
			facilityId,
			subject,
			preparationId,
			localInterviewPort,
			localBufferPort,
			outboxPort,
			onImported,
		],
	);

	const handleFileSelected = React.useCallback(
		(file: File) => {
			if (policy === null) {
				return;
			}

			setRejection(null);
			setPreparationError(null);

			const extensionMatch = /\.[^./]+$/.exec(file.name);
			const extension = extensionMatch !== null ? extensionMatch[0].toLowerCase() : '';
			const format = policy.findFormatByExtension(extension);

			if (format === null) {
				setRejection('unsupported-format');
				return;
			}

			void (async (): Promise<void> => {
				const probe = await audioFilePort.probe(file);

				if (probe.outcome === 'unreadable') {
					setRejection('unreadable');
					return;
				}

				// `durationMs: null` is a NOMINAL probe result, never a rejection on its own
				// (`AudioFilePort.ts`).
				const { durationMs } = probe;
				const maxBytes = policy.maxSegments * policy.segmentMaxBytes;
				const isTooLong = durationMs !== null && durationMs > policy.maxDurationMs;
				const isTooLarge = file.size > maxBytes;

				if (isTooLong || isTooLarge) {
					setRejection('too-large');
					return;
				}

				// `file.type` is derived from the extension by the browser and sometimes empty: only
				// trust it when it already matches one of the policy's accepted MIME types, otherwise
				// fall back to the matched row's own `mimeType` — never an empty string
				// (`recording-policy.ts`).
				const isRecognizedMimeType = policy.acceptedAudioFormats.some(
					(candidate) => candidate.mimeType === file.type,
				);
				const resolvedMimeType =
					file.type !== '' && isRecognizedMimeType ? file.type : format.mimeType;

				await runImport(file, resolvedMimeType, durationMs);
			})().catch(ignoreHandledError);
		},
		[policy, audioFilePort, runImport],
	);

	return {
		policy,
		policyError,
		refetchPolicy,
		rejection,
		preparationError,
		preparingProgress,
		handleFileSelected,
	};
}

export default useInterviewImport;
